import { BaseComponent } from '../base-component.js';
import { api } from '../../client/api.js';
import { escapeHtml } from '../../shared/utils.js';
import type { AnalyticsStats } from '../../shared/types.js';
import componentStyles from './analytics-dashboard.css?inline';

export class AnalyticsDashboard extends BaseComponent {
  private stats: AnalyticsStats | null = null;
  private loading = true;
  private days = 30;

  connectedCallback(): void {
    super.connectedCallback();
    this.loadStats();
  }

  protected styles(): string {
    return componentStyles;
  }

  protected template(): string {
    if (this.loading) {
      return '<app-loader></app-loader>';
    }

    if (!this.stats) {
      return '<div class="empty">Failed to load analytics data</div>';
    }

    const hasData = this.stats.totalPageViews > 0 || this.stats.totalLinkClicks > 0;

    if (!hasData) {
      return `
        <ag-panel>
          <ag-panel-header>
            <h2>Analytics</h2>
          </ag-panel-header>
          <ag-panel-content>
            <div class="empty">
              <p>No analytics data yet. Share your page to start tracking visits and clicks!</p>
            </div>
          </ag-panel-content>
        </ag-panel>
      `;
    }

    return `
      <ag-panel>
        <ag-panel-header>
          <h2>Analytics</h2>
          <p class="description">Here you can see all the activity taking place on your landing page, including visits, link clicks and where they came from (referrals).</p>
        </ag-panel-header>
        <ag-panel-content>
            <div class="date-range">
              <label for="days-select">Time period</label>
              <select id="days-select">
                <option value="7" ${this.days === 7 ? 'selected' : ''}>Last 7 days</option>
                <option value="30" ${this.days === 30 ? 'selected' : ''}>Last 30 days</option>
                <option value="90" ${this.days === 90 ? 'selected' : ''}>Last 90 days</option>
              </select>
            </div>
          <div class="stats-grid">
            <ag-card>
              <ag-card-title>Total Page Views</ag-card-title>
              <ag-card-content fontsize="large">${this.stats.totalPageViews.toLocaleString()}</ag-card-content>
            </ag-card>
            <ag-card>
              <ag-card-title>Total Link Clicks</ag-card-title>
              <ag-card-content fontsize="large">${this.stats.totalLinkClicks.toLocaleString()}</ag-card-content>
            </ag-card>
            <ag-card >
              <ag-card-title>Unique Visitors</ag-card-title>
              <ag-card-content fontsize="large">${this.stats.uniqueVisitors.toLocaleString()}</ag-card-content>
            </ag-card>
          </div>

          <div class="charts-section">
            ${this.renderPageViewsChart()}
            ${this.renderLinkStatsTable()}
            ${this.renderTopReferrers()}
          </div>
        </ag-panel-content>
      </ag-panel>
    `;
  }

  private renderPageViewsChart(): string {
    if (this.stats!.pageViewsByDay.length === 0) {
      return '';
    }

    const maxViews = Math.max(...this.stats!.pageViewsByDay.map((d) => d.views), 1);

    return `
      <ag-card>
        <ag-card-title>Page Views Over Time</ag-card-title>
        <ag-card-content>
          <div class="simple-chart">
            ${this.stats!.pageViewsByDay.map((day) => {
      const height = (day.views / maxViews) * 100;
      const date = new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      return `
                <div class="chart-bar" style="height: ${height}%" title="${day.views} views on ${date}">
                  <span class="chart-bar-label">${date}</span>
                </div>
              `;
    }).join('')}
          </div>
        </ag-card-content>
      </ag-card>
    `;
  }

  private renderLinkStatsTable(): string {
    if (this.stats!.linkStats.length === 0) {
      return '';
    }

    const maxClicks = Math.max(...this.stats!.linkStats.map((l) => l.clicks), 1);

    return `
      <ag-card>
        <ag-card-title>Performance</ag-card-title>
        <ag-card-content>
          <table class="link-stats-table">
          <thead>
            <tr>
              <th>Link</th>
              <th>Clicks</th>
              <th style="width: 40%">Performance</th>
            </tr>
          </thead>
          <tbody>
            ${this.stats!.linkStats.map((link) => {
      const percentage = (link.clicks / maxClicks) * 100;
      return `
                <tr>
                  <td>${escapeHtml(link.linkTitle)}</td>
                  <td>${link.clicks}</td>
                  <td>
                    <div class="bar-container">
                      <div class="bar" style="width: ${percentage}%"></div>
                    </div>
                  </td>
                </tr>
              `;
    }).join('')}
          </tbody>
        </table>
        </ag-card-content>
      </ag-card>
    `;
  }

  private renderTopReferrers(): string {
    if (this.stats!.topReferrers.length === 0) {
      return '';
    }

    return `
      <ag-card>
        <ag-card-title>Top Referrers</ag-card-title>
        <ag-card-content>
          <ul class="referrer-list">
          ${this.stats!.topReferrers.map((ref) => {
      const displayUrl = ref.referrer.length > 50
        ? ref.referrer.substring(0, 50) + '...'
        : ref.referrer;
      return `
              <li class="referrer-item">
                <span class="referrer-url" title="${escapeHtml(ref.referrer)}">${escapeHtml(displayUrl)}</span>
                <span class="referrer-count">${ref.count} visits</span>
              </li>
            `;
    }).join('')}
        </ul>
        </ag-card-content>
      </ag-card>
    `;
  }

  protected afterRender(): void {
    const select = this.$('#days-select') as HTMLSelectElement;
    select?.addEventListener('change', (e) => {
      const target = e.target as HTMLSelectElement;
      this.days = parseInt(target.value);
      this.loadStats();
    });
  }

  private async loadStats(): Promise<void> {
    this.loading = true;
    this.render();

    const result = await api.getAnalyticsStats(this.days);
    if (result.success && result.data) {
      this.stats = result.data;
    }

    this.loading = false;
    this.render();
    this.afterRender();
  }
}

customElements.define('analytics-dashboard', AnalyticsDashboard);
